import { createRouter, createWebHistory } from 'vue-router'
import MainLayout from './layouts/MainLayout.vue'
import BillingLayout from './layouts/BillingLayout.vue'
import { getFirebaseInstances } from './firebase.js'
import HomePage from './views/HomePage.vue'
import OurTeam from './views/OurTeam.vue'
import OurHistory from './views/OurHistory.vue'
import OurSchool from './views/OurSchool.vue'
import OurValues from './views/OurValues.vue'
import GeneralInformation from './views/GeneralInformation.vue'
import EnrolmentProcess from './views/EnrolmentProcess.vue'
import Contact from './views/ContactPage.vue'
import CurriculumPage from './views/CurriculumPage.vue'
import AsaPage from './views/AsaPage.vue'
import DisasterPreparedness from './views/DisasterPreparedness.vue'
import StrategicPlan from './views/StrategicPlan.vue'
import SchoolFees from './views/SchoolFees.vue'
import YumiStrong from './views/YumiStrong.vue'
import OurBoard from './views/OurBoard.vue'
import Employment from './views/EmploymentPage.vue'
import SacePage from './views/SacePage.vue'
import WondeSync from './views/WondeSync.vue'
import BillingLogin from './views/LoginPage.vue'
import BillingDashboard from './views/BillingDashboard.vue'
import BillingSettings from './views/BillingSettings.vue'
import BillingContact from './views/BillingContact.vue'
import SchoolYearbook from './views/SchoolYearbook.vue'
import BillingYearly from './views/BillingYearly.vue'
import BillingUpdateYearLevel from './views/BillingUpdateYearLevel.vue'
import WithdrawStudents from './views/WithdrawStudents.vue'
import InvoiceValidation from './views/InvoiceValidation.vue'
import BoardSalaryReview from './views/BoardSalaryReview.vue'
import BoardAccessLog from './views/BoardAccessLog.vue'

const routes = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
    path: '/',
    name: 'Homepage',
    component: HomePage
  },
  {
    path: '/our-team',
    name: 'OurTeam',
    component: OurTeam
  },
  {
    path: '/our-board',
    name: 'OurBoard',
    component: OurBoard
  },
  {
    path: '/our-history',
    name: 'OurHistory',
    component: OurHistory
  },
  {
    path: '/our-school',
    name: 'OurSchool',
    component: OurSchool
  },
  {
    path: '/our-values',
    name: 'OurValues',
    component: OurValues
  },
  {
    path: '/our-curriculum',
    name: 'CurriculumPage',
    component: CurriculumPage
  },
  {
    path: '/asa',
    name: 'After School Activities',
    component: AsaPage
  },
  {
    path: '/disaster-preparedness',
    name: 'Disaster Preparedness',
    component: DisasterPreparedness
  },
  {
    path: '/strategic-plan',
    name: 'Strategic Plan',
    component: StrategicPlan
  },
  {
    path: '/yearbook',
    name: 'PVIS Yearbook',
    component:SchoolYearbook
  },
  {
    path: '/school-fees',
    name: 'School Fees',
    component: SchoolFees
  },
  {
    path: '/yumi-strong',
    name: 'Yumi Strong',
    component: YumiStrong
  },
  {
    path: '/general-information',
    name: 'GeneralInformation',
    component: GeneralInformation
  },
  {
    path: '/enrolment-process',
    name: 'EnrolmentProcess',
    component: EnrolmentProcess
  },
  {
    path: '/sace',
    name: 'SacePage',
    component: SacePage
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact
  },
  {
    path: '/employment',
    name: 'Employment',
    component: Employment
  },
]
},
{
  path: '/billing',
  component: BillingLayout,
  children: [
    {
      path: '', // This matches /billing directly
      redirect: '/billing/login' // This redirects to /billing/login
    },
    {
      path: 'dashboard',
      component: BillingDashboard,
      meta: { requiresAuth: true } // Add this line
    },
    {
      path: 'yearly',
      component: BillingYearly,
      meta: { requiresAuth: true }
    },
    {
      path: 'validation',
      component: InvoiceValidation,
      meta: { requiresAuth: true }
    },
    {
      path: 'contact',
      component: BillingContact,
      meta: { requiresAuth: true }
    },
    {
      path: 'settings',
      component: BillingSettings,
      meta: { requiresAuth: true }
    },
    {
      path: 'updateyearlevel',
      component: BillingUpdateYearLevel,
      meta: { requiresAuth: true }
    },
    {
      path: 'login',
      component: BillingLogin
    },
    {
      path: 'wonde-sync',
      name: 'WondeSync',
      component: WondeSync,
      meta: { requiresAuth: true }
    },
    {
      path: 'withdraw-students',
      name: 'WithdrawStudents',
      component: WithdrawStudents,
      meta: { requiresAuth: true }
    },
]
},
{
  // Password-gated inside the view itself (shared board password checked
  // server-side; the salary dataset never ships in the public bundle).
  path: '/board-review',
  name: 'BoardSalaryReview',
  component: BoardSalaryReview
},
{
  // Admin-only access log: requires the board password AND the separate
  // BOARD_REVIEW_ADMIN_KEY, both verified server-side.
  path: '/board-review/access',
  name: 'BoardAccessLog',
  component: BoardAccessLog
}
]

let routerInstance = null;
let routerInitializationPromise = null;

// Function to create and initialize the router after Firebase is ready
async function initializeRouter() {
  try {
    // Wait for Firebase auth to be initialized
    const { auth } = await getFirebaseInstances(); 

    return new Promise(resolve => {
      // Use onAuthStateChanged just to trigger router creation after initial check
      const unsubscribe = auth.onAuthStateChanged(() => {
        if (!routerInstance) { 
          // Create the router only once
          routerInstance = createRouter({
            history: createWebHistory(process.env.BASE_URL),
            routes
          });

          // Setup the navigation guard
          routerInstance.beforeEach((to, from, next) => {
            const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
            const isAuthenticated = !!auth.currentUser; // Check current user status directly from auth

            if (requiresAuth && !isAuthenticated) {
              // Only log verbose messages in development
              if (process.env.NODE_ENV !== 'production') {
                console.log('Navigation blocked: requires auth, user not authenticated. Redirecting to login.');
              }
              next('/billing/login');
            } else {
              next();
            }
          });
          // Only log verbose messages in development
          if (process.env.NODE_ENV !== 'production') {
            console.log('Router initialized and guards set up.');
          }
          resolve(routerInstance); // Resolve the promise with the router instance
        }
        unsubscribe(); // Unsubscribe after the initial state is known and router is created
      });
    });
  } catch (error) {
    console.error("Failed to initialize router due to Firebase error:", error); // Keep error logging for prod
    // Handle router initialization failure (e.g., show error message, prevent app load)
    // Depending on the app structure, you might want to reject the promise
    // or resolve with null/error state to be handled in main.js
    throw new Error("Router initialization failed"); 
  }
}

// Export a function that ensures the router is initialized
export default () => {
    if (!routerInitializationPromise) {
        routerInitializationPromise = initializeRouter();
    }
    return routerInitializationPromise;
};